import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Target, Trophy, ArrowRight, Clock, Shield } from "lucide-react";

const API = '/api';

export default function TodaysEvents() {
  const [data, setData] = useState({ date: '', events: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTodaysEvents();
  }, []);

  const fetchTodaysEvents = async () => {
    try {
      const res = await axios.get(`${API}/events/today`);
      setData(res.data);
    } catch (error) {
      console.error("Error fetching today's events:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div data-testid="todays-events-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            Today's Events
          </h1>
          <p className="text-slate-500 mt-1 flex items-center gap-2">
            <Calendar size={16} />
            {formatDate(data.date)}
          </p>
        </div>
        <Link to="/events">
          <Button variant="outline">View All Events</Button>
        </Link>
      </div>

      {data.events.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="mx-auto text-slate-300 mb-4" size={48} />
            <h3 className="text-lg font-medium text-slate-700">No Events Today</h3>
            <p className="text-slate-500 mt-2">Check back later or view all events.</p>
            <Link to="/events" className="inline-block mt-4">
              <Button>Browse All Events</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {data.events.map((event) => (
            <Card key={event.id} className="overflow-hidden" data-testid={`event-${event.id}`}>
              <CardHeader className="bg-[#1B3B6F] text-white pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-serif text-xl">{event.name}</CardTitle>
                  <Badge className="bg-white/20 text-white">
                    {event.matches?.length || 0} Matches
                  </Badge>
                </div>
                {event.description && (
                  <p className="text-white/80 text-sm mt-1">{event.description}</p>
                )}
              </CardHeader>
              <CardContent className="p-0">
                {event.matches && event.matches.length > 0 ? (
                  <div className="divide-y">
                    {event.matches.map((match) => (
                      <div 
                        key={match.id} 
                        className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                        data-testid={`match-${match.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${match.is_protection ? 'bg-purple-100' : 'bg-green-100'}`}>
                            {match.is_protection ? (
                              <Shield className="text-purple-600" size={20} />
                            ) : (
                              <Target className="text-green-600" size={20} />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">
                              {match.name}
                              {match.match_number && (
                                <span className="ml-2 text-xs bg-slate-100 px-2 py-0.5 rounded">
                                  #{match.match_number}
                                </span>
                              )}
                            </p>
                            <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                              {match.is_protection && (
                                <span className="text-purple-600 font-medium">Protection (2-Person Team)</span>
                              )}
                              <span>{match.shots_per_round} shots</span>
                              <span>${parseFloat(match.entry_fee || 0).toFixed(2)} entry</span>
                              {match.start_time && (
                                <span className="flex items-center gap-1">
                                  <Clock size={12} />
                                  {new Date(match.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Link to={`/leaderboard?match=${match.id}`}>
                            <Button variant="outline" size="sm" data-testid={`leaderboard-${match.id}`}>
                              <Trophy size={14} className="mr-1" />
                              Leaderboard
                            </Button>
                          </Link>
                          <Link to={`/score-entry?match=${match.id}`}>
                            <Button size="sm" className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90" data-testid={`score-entry-${match.id}`}>
                              Enter Scores
                              <ArrowRight size={14} className="ml-1" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-center text-slate-500">
                    No matches scheduled for this event yet.
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
